// App.tsx
import { useState, useEffect } from 'react';
import FileUpload from './components/FileUpload';
import ReviewResult from './components/ReviewResult';
import HistoryList from './components/HistoryList';
import { ReviewResult as ReviewResultType, ReviewStatus } from './types';
import { uploadAndReview, getToken, getUsername, logout } from './api';

type View = 'upload' | 'history';

export default function App() {
  const [view, setView] = useState<View>('upload');
  const [status, setStatus] = useState<ReviewStatus>('idle');
  const [result, setResult] = useState<ReviewResultType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ username: string } | null>(null);

  // Check auth on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      window.location.href = '/';
      return;
    }
    const username = getUsername();
    if (username) {
      setUser({ username });
    }
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // ── Upload ──
  const handleUpload = async (file: File, standard: string) => {
    setStatus('uploading');
    setError(null);

    try {
      setStatus('reviewing');
      const data = await uploadAndReview(file, standard);
      setResult(data);
      setStatus('done');
    } catch (err: any) {
      setError(err.message || '审查失败，请重试');
      setStatus('error');
    }
  };

  const handleNewReview = () => {
    setView('upload');
    setStatus('idle');
    setResult(null);
    setError(null);
  };

  // ── History view ──
  if (view === 'history') {
    return <HistoryList onBack={() => setView('upload')} />;
  }

  // ── Review result view ──
  if (status === 'done' && result) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className="flex gap-3 mb-4 justify-between">
            <div className="flex gap-3">
              <button
                onClick={handleNewReview}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                📄 新审查
              </button>
              <button
                onClick={() => setView('history')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                📋 审查历史
              </button>
            </div>
            <div className="flex items-center gap-3">
              <a href="/" className="text-xs text-gray-400 hover:text-blue-600 transition">← 返回工作台</a>
              <span className="text-sm text-gray-500">👤 {user?.username}</span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-400 hover:text-red-500 transition"
              >
                退出
              </button>
            </div>
          </div>
        </div>
        <ReviewResult result={result} />
      </div>
    );
  }

  // ── Upload view ──
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between mb-4">
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-gray-400 hover:text-blue-600 transition">← 返回工作台</a>
            <span className="text-sm text-gray-500">👤 {user?.username}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-400 hover:text-red-500 transition"
            >
              退出
            </button>
          </div>
          <button
            onClick={() => setView('history')}
            className="px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
          >
            📋 审查历史
          </button>
        </div>
      </div>

      <FileUpload
        onUpload={handleUpload}
        disabled={status === 'uploading' || status === 'reviewing'}
      />

      {status === 'reviewing' && (
        <div className="text-center mt-8">
          <div className="inline-block animate-spin text-4xl mb-4">⚙️</div>
          <p className="text-gray-600">AI 正在审查合同，请稍候...</p>
          <p className="text-sm text-gray-400 mt-1">复杂合同可能需要 30-60 秒</p>
        </div>
      )}

      {error && (
        <div className="max-w-2xl mx-auto mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">❌ {error}</p>
          <button
            onClick={() => { setStatus('idle'); setError(null); }}
            className="mt-2 text-sm text-red-600 hover:underline"
          >
            重试
          </button>
        </div>
      )}
    </div>
  );
}